import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Pencil, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import {
  BASE, ICON, fmtINR, fmtNum, statusOf, exportPoToExcel, ConfirmModal,
  DpoBeamBackground, EMBEDDED_CSS, S,
} from './distributorPoShared';

/* ============================================================================
 * Dedicated "My Recent Purchase Orders" page — a real page (own URL), not a
 * popup, with filters. Replaces the old history modal that used to live
 * inside DistributorPO.jsx. Status-change actions (Sales Order / Proforma
 * Invoice / Reject) and exporting a saved PO to Excel live here now;
 * creating/editing a PO still happens on DistributorPO.jsx (Edit deep-links
 * there via ?edit=<po_number>).
 * ========================================================================== */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Awaiting distributor approval' },
  { value: 'sales_order', label: 'Sent as Sales Order' },
  { value: 'proforma_invoice', label: 'Sent as Proforma Invoice' },
  { value: 'rejected', label: 'Rejected' },
];

export default function DistributorPOHistory({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kamName = user?.display_name || user?.username || 'KAM Portal';

  const [distributors, setDistributors] = useState([]);
  const [platforms, setPlatforms]       = useState([]);
  const [filterDistributor, setFilterDistributor] = useState('');
  const [filterPlatform, setFilterPlatform]       = useState('');
  const [filterStatus, setFilterStatus]           = useState(searchParams.get('status') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }

  useEffect(() => {
    fetch(`${BASE}/distributor-po?action=distributors`)
      .then(r => r.json()).then(d => setDistributors(Array.isArray(d) ? d : []))
      .catch(() => setDistributors([]));
  }, []);

  useEffect(() => {
    setFilterPlatform(''); setPlatforms([]);
    if (!filterDistributor) return;
    fetch(`${BASE}/distributor-po?action=platforms&distributor=${encodeURIComponent(filterDistributor)}`)
      .then(r => r.json()).then(d => setPlatforms(Array.isArray(d) ? d : []))
      .catch(() => setPlatforms([]));
  }, [filterDistributor]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'history', created_by: kamName });
      if (filterDistributor) params.set('distributor', filterDistributor);
      if (filterPlatform)    params.set('platform', filterPlatform);
      if (filterStatus)      params.set('status', filterStatus);
      if (dateFrom)          params.set('date_from', dateFrom);
      if (dateTo)            params.set('date_to', dateTo);
      const res  = await fetch(`${BASE}/distributor-po?${params.toString()}`);
      const data = await res.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [kamName, filterDistributor, filterPlatform, filterStatus, dateFrom, dateTo]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  async function setOrderType(poNumber, orderType) {
    try {
      const res  = await fetch(`${BASE}/distributor-po?action=set-order-type`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: poNumber, order_type: orderType }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not update PO');
      showToast(`${poNumber} sent to Accounts as ${orderType}`);
      loadHistory();
    } catch (e) { showToast('Network error updating PO'); }
  }

  async function rejectPo(poNumber) {
    try {
      const res  = await fetch(`${BASE}/distributor-po?action=reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: poNumber }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not reject PO');
      showToast(`${poNumber} rejected and removed`);
      loadHistory();
    } catch (e) { showToast('Network error rejecting PO'); }
  }

  async function runConfirmedAction() {
    if (!confirmModal) return;
    const { poNumber, kind, value } = confirmModal;
    setConfirmModal(null);
    if (kind === 'reject') await rejectPo(poNumber);
    else await setOrderType(poNumber, value);
  }

  async function downloadExcelFor(poNumber) {
    try {
      const res  = await fetch(`${BASE}/distributor-po?action=detail&po_number=${encodeURIComponent(poNumber)}`);
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Could not load PO');
      exportPoToExcel(data);
    } catch (e) { showToast('Network error loading PO'); }
  }

  return (
    <div className="dpo-page" style={S.page}>
      <style>{EMBEDDED_CSS}</style>

      <div style={S.headerBar}>
        <DpoBeamBackground />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          <button style={S.backBtn} onClick={() => navigate('/dms/distributor-po')}><ArrowLeft size={ICON.sm} />Back</button>
          <h1 style={{ ...S.h1 }}>My Recent <span style={{ fontWeight: 600, color: '#C9B7EA' }}>Purchase Orders</span></h1>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.filterBar}>
          <div style={S.filterField}>
            <div style={S.statLabel}>Distributor</div>
            <select style={S.select} value={filterDistributor} onChange={e => setFilterDistributor(e.target.value)}>
              <option value="">All distributors</option>
              {distributors.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={S.filterField}>
            <div style={S.statLabel}>Platform</div>
            <select style={S.select} value={filterPlatform} disabled={!filterDistributor} onChange={e => setFilterPlatform(e.target.value)}>
              <option value="">{filterDistributor ? 'All platforms' : 'Pick a distributor first'}</option>
              {platforms.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={S.filterField}>
            <div style={S.statLabel}>Status</div>
            <select style={S.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={S.filterField}>
            <div style={S.statLabel}>From</div>
            <input type="date" style={S.select} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div style={S.filterField}>
            <div style={S.statLabel}>To</div>
            <input type="date" style={S.select} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>

        {loading ? (
          <div style={{ color: 'var(--dpo-text-muted)', padding: '20px 0', textAlign: 'center' }}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{ color: 'var(--dpo-text-muted)', padding: '20px 0', textAlign: 'center' }}>No Purchase Orders match these filters</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {history.map(h => {
              const st = statusOf(h);
              const pending = h.status === 'pending' && !h.order_type;
              return (
                <div key={h.po_number} style={S.historyRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--dpo-purple)', fontFamily: 'monospace' }}>{h.po_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--dpo-text-muted)' }}>{h.party_name}{h.channel ? ` · ${h.channel}` : ''}{h.location ? ` · ${h.location}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13 }}>
                      <div>{fmtNum(h.po_units)} units</div>
                      <div style={{ fontWeight: 700 }}>{fmtINR(h.po_amount)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ ...S.statusBadge, background: st.tint, color: st.color }}>{st.label}</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={S.smallActionBtn} onClick={() => downloadExcelFor(h.po_number)}><Download size={ICON.xs} />Download Excel</button>
                      {pending && (
                        <>
                          <button style={S.smallActionBtn} onClick={() => navigate(`/dms/distributor-po?edit=${encodeURIComponent(h.po_number)}`)}><Pencil size={ICON.xs} />Edit</button>
                          <button style={{ ...S.smallActionBtn, color: 'var(--dpo-green)' }} onClick={() => setConfirmModal({ poNumber: h.po_number, kind: 'order_type', value: 'Sales Order' })}><CheckCircle2 size={ICON.xs} />Sales Order</button>
                          <button style={{ ...S.smallActionBtn, color: 'var(--dpo-green)' }} onClick={() => setConfirmModal({ poNumber: h.po_number, kind: 'order_type', value: 'Proforma Invoice' })}><CheckCircle2 size={ICON.xs} />Proforma Invoice</button>
                          <button style={{ ...S.smallActionBtn, color: 'var(--dpo-error)' }} onClick={() => setConfirmModal({ poNumber: h.po_number, kind: 'reject' })}><XCircle size={ICON.xs} />Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal config={confirmModal} onCancel={() => setConfirmModal(null)} onConfirm={runConfirmedAction} />

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}
